import { useEffect, useRef, useState } from "react";
import { PALETTE, type NodeRelationshipDto } from "@mmm/contracts";
import { Canvas } from "./Canvas.js";
import { DEFAULT_VIEWPORT, fitToContent } from "./mapViewport.js";
import { MapToolbar } from "./MapToolbar.js";
import { WritingPanel } from "./WritingPanel.js";
import { useMapEditing, type NodeSize } from "./MapEditingContext.js";
import type { DisplayRelationship } from "./RelationshipEdgeSvg.js";
import { LoadingRows, QueryStatus } from "../ui/LoadingPlaceholders.js";
import { useQuery } from "../../api/useQuery.js";
import { getMindMap } from "../../api/queries.js";
import { createNode, createNodeRelationship, deleteNodeRelationship, fireCommand } from "../../api/commands.js";
import { useConfirm } from "../shell/ConfirmProvider.js";
import { updateNodeFields } from "../../api/nodeUpdates.js";
import { DEFAULT_NODE_WIDTH, DEFAULT_NODE_HEIGHT } from "../../constants.js";
import { useGridSettings } from "../../preferences/GridSettingsProvider.js";
import { EmptyState, ErrorState, useT } from "@mmm/ui";
function sameRelationshipPair(a: { fromNodeId: string; toNodeId: string }, b: { fromNodeId: string; toNodeId: string }) {
  return a.fromNodeId === b.fromNodeId && a.toNodeId === b.toNodeId;
}

function relationshipPairKey(relationship: { fromNodeId: string; toNodeId: string }) {
  return `${relationship.fromNodeId}\u0000${relationship.toNodeId}`;
}

/** The editor for one mind map — the single map a subject or chapter owns. */
export function MapEditor({ mindMapId }: { mindMapId: string }) {
  const mapQuery = useQuery(getMindMap, { mindMapId });
  const { data: aggregate } = mapQuery;
  const confirm = useConfirm();
  const t = useT();
  const { gridSize } = useGridSettings();

  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [nodeSizePreview, setNodeSizePreview] = useState<(NodeSize & { nodeId: string }) | null>(null);
  const [pendingRelationships, setPendingRelationships] = useState<DisplayRelationship[]>([]);
  const [connectMode, setConnectMode] = useState(false);
  const [connectFromId, setConnectFromId] = useState<string | null>(null);
  const [pan, setPan] = useState(DEFAULT_VIEWPORT.pan);
  const [zoom, setZoom] = useState(DEFAULT_VIEWPORT.zoom);
  const boardRef = useRef<HTMLDivElement>(null);
  /** Avoid re-framing after data refreshes or edits at an unchanged board size. */
  const fittedBoardRef = useRef<{ mapId: string; width: number; height: number } | null>(null);
  const seededEmptyMapRef = useRef<string | null>(null);
  const sizePreviewRevisionRef = useRef(0);
  const pendingRelationshipPairsRef = useRef(new Set<string>());
  const selectedNode = aggregate?.nodes.find((n) => n.id === selectedNodeId);
  const displayRelationships = aggregate
    ? [
        ...aggregate.relationships,
        ...pendingRelationships.filter(
          (pending) => !aggregate.relationships.some((relationship) => sameRelationshipPair(relationship, pending))
        ),
      ]
    : [];

  // Re-frame when the board changes size (including phone rotation), while
  // leaving the learner's pan and zoom alone during ordinary map edits.
  useEffect(() => {
    const nodes = aggregate?.nodes;
    const board = boardRef.current;
    if (!nodes || nodes.length === 0 || !board) return;

    const fitBoard = () => {
      const width = board.clientWidth;
      const height = board.clientHeight;
      if (width <= 0 || height <= 0) return;
      const previous = fittedBoardRef.current;
      if (previous?.mapId === mindMapId && previous.width === width && previous.height === height) return;
      fittedBoardRef.current = { mapId: mindMapId, width, height };
      const fitted = fitToContent(nodes, width, height);
      setPan(fitted.pan);
      setZoom(fitted.zoom);
    };

    fitBoard();
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(fitBoard);
      observer.observe(board);
      return () => observer.disconnect();
    }
    window.addEventListener("resize", fitBoard);
    return () => window.removeEventListener("resize", fitBoard);
  }, [aggregate, mindMapId]);

  // The aggregate is authoritative after a command invalidates the query.
  // Reconcile each optimistic edge independently so concurrent saves survive.
  useEffect(() => {
    if (!aggregate) return;
    const confirmedPairs = new Set(aggregate.relationships.map(relationshipPairKey));
    for (const pair of confirmedPairs) pendingRelationshipPairsRef.current.delete(pair);
    if (pendingRelationships.some((pending) => confirmedPairs.has(relationshipPairKey(pending)))) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPendingRelationships((current) => current.filter((pending) => !confirmedPairs.has(relationshipPairKey(pending))));
    }
  }, [aggregate, pendingRelationships]);

  // Keep the committed size over stale query data until the server aggregate
  // reports the same dimensions, preventing a flash back to the old size.
  useEffect(() => {
    if (!aggregate || !nodeSizePreview) return;
    const node = aggregate.nodes.find((item) => item.id === nodeSizePreview.nodeId);
    if (node?.width === nodeSizePreview.width && node.height === nodeSizePreview.height) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setNodeSizePreview((current) => (current === nodeSizePreview ? null : current));
    }
  }, [aggregate, nodeSizePreview]);

  useEffect(() => {
    const cancelConnect = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setConnectMode(false);
      setConnectFromId(null);
    };
    window.addEventListener("keydown", cancelConnect);
    return () => window.removeEventListener("keydown", cancelConnect);
  }, []);

  // Publish selected-node controls to the sidebar while a map is being edited.
  const { setSession } = useMapEditing();
  useEffect(() => {
    setSession({
      color: selectedNode?.color ?? PALETTE[0],
      hasSelection: Boolean(selectedNode),
      selectedNode: selectedNode ?? null,
      onCloseSelection: () => {
        sizePreviewRevisionRef.current += 1;
        setSelectedNodeId(null);
        setNodeSizePreview(null);
      },
      onPick: (color) => {
        if (selectedNode) fireCommand(updateNodeFields(selectedNode, { color }));
      },
      onPreviewSize: (size) => {
        if (selectedNode) {
          sizePreviewRevisionRef.current += 1;
          setNodeSizePreview({ nodeId: selectedNode.id, ...size });
        }
      },
      onCommitSize: (size) => {
        if (!selectedNode) return;
        const preview = { nodeId: selectedNode.id, ...size };
        const revision = ++sizePreviewRevisionRef.current;
        if (selectedNode.width === size.width && selectedNode.height === size.height) {
          setNodeSizePreview(null);
          return;
        }
        setNodeSizePreview(preview);
        fireCommand(
          updateNodeFields(selectedNode, size).catch((error: unknown) => {
            if (sizePreviewRevisionRef.current === revision) {
              setNodeSizePreview((current) => (current === preview ? null : current));
            }
            throw error;
          })
        );
      },
    });
  }, [setSession, selectedNode]);
  useEffect(() => () => setSession(null), [setSession]);

  useEffect(() => {
    if (!aggregate || aggregate.nodes.length > 0 || seededEmptyMapRef.current === mindMapId) return;
    seededEmptyMapRef.current = mindMapId;
    fireCommand(
      createNode({
        mindMapId,
        title: aggregate.mindMap.title,
        x: 360,
        y: 220,
        width: DEFAULT_NODE_WIDTH,
        height: DEFAULT_NODE_HEIGHT,
        zIndex: 10,
        color: PALETTE[0],
        collapsed: false,
        connectFromNodeId: null,
      })
    );
  }, [aggregate, mindMapId]);

  if (mapQuery.error && !mapQuery.waiting) {
    return <ErrorState title={t("maps.loadFailed")} onRetry={mapQuery.retry} />;
  }

  if (!aggregate) {
    return (
      <div>
        <QueryStatus queries={[mapQuery]} />
        <div className="mmm-card" style={{ padding: 20, maxWidth: 720 }}>
          <LoadingRows rows={5} />
        </div>
      </div>
    );
  }

  const handleAddNode = async () => {
    // MapToolbar invokes this directly (not awaited) — the try/catch keeps a
    // failed createNode from surfacing as an unhandled promise rejection;
    // it's already toasted centrally by api/commands.ts's run().
    try {
      const parent = selectedNode || aggregate.nodes[0];
      const angle = Math.random() * Math.PI * 2;
      const radius = 180;
      const x = parent ? parent.x + Math.cos(angle) * radius : 400;
      const y = parent ? parent.y + Math.sin(angle) * radius : 200;

      const result = await createNode({
        mindMapId,
        title: t("maps.newNode"),
        x,
        y,
        width: DEFAULT_NODE_WIDTH,
        height: DEFAULT_NODE_HEIGHT,
        zIndex: 10,
        color: PALETTE[0],
        collapsed: false,
        connectFromNodeId: selectedNode ? selectedNode.id : null,
      });
      const newNode = result.nodes[result.nodes.length - 1];
      setSelectedNodeId(newNode.id);
      const board = boardRef.current;
      if (board) {
        const margin = 16;
        const left = newNode.x * zoom + pan.x;
        const top = newNode.y * zoom + pan.y;
        const width = newNode.width * zoom;
        const height = newNode.height * zoom;
        setPan({
          x: width + margin * 2 > board.clientWidth
            ? (board.clientWidth - width) / 2 - newNode.x * zoom
            : pan.x + Math.max(margin - left, 0) - Math.max(left + width + margin - board.clientWidth, 0),
          y: height + margin * 2 > board.clientHeight
            ? (board.clientHeight - height) / 2 - newNode.y * zoom
            : pan.y + Math.max(margin - top, 0) - Math.max(top + height + margin - board.clientHeight, 0),
        });
      }
    } catch {
      // Already toasted — nothing left to do.
    }
  };

  const handleNodeClick = (node: { id: string }) => {
    if (!connectMode) {
      setNodeSizePreview(null);
      setSelectedNodeId(node.id);
      return;
    }
    if (!connectFromId) {
      setConnectFromId(node.id);
      return;
    }
    if (connectFromId !== node.id) {
      const pair = { fromNodeId: connectFromId, toNodeId: node.id };
      const pairKey = relationshipPairKey(pair);
      const alreadyExists = aggregate.relationships.some((relationship) => sameRelationshipPair(relationship, pair));
      if (!alreadyExists && !pendingRelationshipPairsRef.current.has(pairKey)) {
        pendingRelationshipPairsRef.current.add(pairKey);
        const input = {
          mindMapId,
          fromNodeId: connectFromId,
          toNodeId: node.id,
          type: "related" as const,
          label: "",
          curve: "auto",
          labelX: null,
          labelY: null,
          stroke: "#c4bedd",
          zIndex: 10,
        };
        const now = new Date().toISOString();
        const optimisticRelationship: DisplayRelationship = {
          ...input,
          id: `pending:${connectFromId}:${node.id}`,
          createdAt: now,
          updatedAt: now,
          pending: true,
        };
        setPendingRelationships((current) => [...current, optimisticRelationship]);
        fireCommand(
          createNodeRelationship(input)
            .catch((error: unknown) => {
              pendingRelationshipPairsRef.current.delete(pairKey);
              setPendingRelationships((current) => current.filter((relationship) => relationship.id !== optimisticRelationship.id));
              throw error;
            })
        );
      }
    }
    setConnectFromId(null);
    setConnectMode(false);
  };

  const handleDeleteRelationship = async (relationship: NodeRelationshipDto) => {
    const pairKey = relationshipPairKey(relationship);
    pendingRelationshipPairsRef.current.delete(pairKey);
    setPendingRelationships((current) => current.filter((pending) => !sameRelationshipPair(pending, relationship)));
    const from = aggregate.nodes.find((node) => node.id === relationship.fromNodeId);
    const to = aggregate.nodes.find((node) => node.id === relationship.toNodeId);
    const ok = await confirm({
      title: t("maps.deleteConnection"),
      message:
        from && to
          ? t("maps.deleteConnectionNamedMessage", { from: from.title, to: to.title })
          : t("maps.deleteConnectionMessage"),
      confirmLabel: t("maps.deleteConnection"),
    });
    if (!ok) return;
    try {
      await deleteNodeRelationship({ relationshipId: relationship.id });
    } catch {
      // Already toasted — Canvas invokes this handler without awaiting it.
    }
  };

  return (
    <div>
      <MapToolbar
        onAddNode={handleAddNode}
        canAddNode
        connectMode={connectMode}
        onToggleConnect={() => {
          setConnectMode((value) => !value);
          setConnectFromId(null);
        }}
        onZoomIn={() => setZoom((z) => Math.min(2, z + 0.1))}
        onZoomOut={() => setZoom((z) => Math.max(0.5, z - 0.1))}
        onCenter={() => {
          const board = boardRef.current;
          if (!board) return;
          const fitted = fitToContent(aggregate?.nodes ?? [], board.clientWidth, board.clientHeight);
          setPan(fitted.pan);
          setZoom(fitted.zoom);
        }}
      />

      {aggregate.nodes.length === 0 ? (
        <EmptyState
          title={t("maps.preparingCanvas")}
          message={t("maps.preparingCanvasMessage")}
        />
      ) : (
        <div className="mmm-map-editor">
          <Canvas
            nodes={aggregate.nodes}
            nodeSizePreview={nodeSizePreview}
            relationships={displayRelationships}
            pan={pan}
            zoom={zoom}
            gridSize={gridSize}
            boardRef={boardRef}
            onPanChange={setPan}
            selectedNodeId={selectedNodeId}
            connectMode={connectMode}
            connectFromId={connectFromId}
            onNodeClick={handleNodeClick}
            onBlankClick={() => {
              sizePreviewRevisionRef.current += 1;
              setSelectedNodeId(null);
              setNodeSizePreview(null);
            }}
            onNodeRename={(node, title) => fireCommand(updateNodeFields(node, { title }))}
            onNodeMove={(nodeId, x, y) => {
              // Returned (not fireCommand-wrapped) so Canvas can revert its
              // optimistic drag-position overlay for this node if the save
              // fails — see the comment on `dragPositions` in Canvas.tsx.
              const node = aggregate.nodes.find((n) => n.id === nodeId);
              return node ? updateNodeFields(node, { x, y }) : undefined;
            }}
            onRelationshipDelete={handleDeleteRelationship}
          />
        </div>
      )}
      <WritingPanel
        key={selectedNode?.id ?? `map-writing:${mindMapId}`}
        node={selectedNode ?? null}
        mindMapId={mindMapId}
        mindMapMarkdown={aggregate.mindMapMarkdown}
        isChapter={aggregate.mindMap.chapterId !== null}
      />
    </div>
  );
}
